import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, DialogActions, DialogContent } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TCategoryFormData, TFormType } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import FormProvider, { RHFSelect, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { FormInnerContainer, FormItem, FormLabel, FormRow } from 'src/layouts/form/FormLayout';
import { ICategory, useStores } from 'src/models';
import { categoryApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import categoryFormValidationSchema from 'src/static/yup/categoryFormValidationSchema';
import { getCategoryFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import removeNullUndefined from 'src/utils/removeNullUndefined';

import { buildCategoryOptions } from '../utils/bulidCategoryOptions';

interface ICategoryFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: ICategory;
}

export const CategoryFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: ICategoryFormDialogProps) => {
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore, categoryStore } = useStores();
    const { formId } = useDialogContext();
    const isCreate = formType === FormType.CREATE;
    // 상위 카테고리와 자식 카테고리까지 포함한 선택 옵션 생성
    const CTEGRY_SELECT_OPTIONS = [
      { value: 0, label: '없음' },
      ...buildCategoryOptions(categoryStore.categoryTree),
    ];

    const methods = useForm<TCategoryFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      resolver: yupResolver(categoryFormValidationSchema),
      defaultValues: getCategoryFormDefaultValues(selectedData),
    });
    const { reset, watch } = methods;

    // 최상위 카테고리 선택했을 경우 카테고리명, 제목, 설명만 입력하도록 제한
    // 현재 선택된 상위 카테고리 정보 조회
    const selectedPCategoryData = useMemo(() => {
      return categoryStore.categories.find(
        (category) => category.categorySid === watch('pCategory'),
      );
    }, [watch('pCategory'), categoryStore.categories]);

    // 최상위 카테고리 여부 확인
    const isTopCategory = watch('pCategory') === 0;

    // 등록 / 수정 함수
    const onValid = async (data: TCategoryFormData) => {
      const formData = {
        ...removeNullUndefined(data),
      };
      const responseText = isCreate ? '등록' : '수정';
      const result =
        !isCreate && selectedData
          ? categoryApi.put(selectedData.categorySid, formData)
          : categoryApi.post(formData);
      if (await result) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      } else {
        console.error(`${responseText} 실패`);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        categoryApi
          .remove(selectedData?.categorySid)
          .then(() =>
            handleResponse(
              '카테고리 삭제가 완료되었습니다.',
              '카테고리 삭제가 실패되었습니다.',
              onSubmitSuccess,
            ),
          );
      });
      alertStore.setProps({
        open: true,
        title: '카테고리 삭제',
        content: '해당 카테고리을 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    // 상위 카테고리 변경 시 필드 reset + 선택한 pCategory 값 적용
    useEffect(() => {
      reset({
        ...getCategoryFormDefaultValues(selectedData),
        pCategory: selectedPCategoryData?.categorySid || 0,
      });
    }, [watch('pCategory'), isTopCategory]);

    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          <FormRow sx={{ gridTemplateColumns: '1fr 1fr' }}>
            <FormItem>
              <RHFSelect
                label={t('category.form.pCategory')}
                name="pCategory"
                options={CTEGRY_SELECT_OPTIONS}
                useAll={false}
              />
            </FormItem>
            <FormItem>
              <RHFSwitch label={t('category.form.useYn')} name="useYn" />
            </FormItem>
          </FormRow>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, width: '100%' }}>
            <FormInnerContainer sx={{ flexGrow: 1 }}>
              <FormLabel>{t('category.form.korean.name')}</FormLabel>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextField label={t('category.form.korean.categoryNm')} name="categoryNm" />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextField label={t('category.form.korean.mainTitle')} name="mainTitle" />
                </FormItem>
              </FormRow>
              {!isTopCategory && (
                <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                  <FormItem>
                    <RHFTextField label={t('category.form.korean.subTitle')} name="subTitle" />
                  </FormItem>
                </FormRow>
              )}
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextField label={t('category.form.korean.mainDesc')} name="mainDesc" />
                </FormItem>
              </FormRow>
              {!isTopCategory && (
                <>
                  <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                    <FormItem>
                      <RHFTextField label={t('category.form.korean.subDesc')} name="subDesc" />
                    </FormItem>
                  </FormRow>
                  <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                    <FormItem>
                      <RHFTextField label={t('category.form.korean.pipeline')} name="pipeline" />
                    </FormItem>
                  </FormRow>
                  <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                    <FormItem>
                      <RHFTextField
                        name="pipelineList"
                        multiline
                        rows={7}
                        placeholder={`${t('category.form.korean.pipelineList')}`}
                      />
                    </FormItem>
                  </FormRow>
                </>
              )}
            </FormInnerContainer>
            <FormInnerContainer sx={{ flexGrow: 1 }}>
              <FormLabel>{t('category.form.english.name')}</FormLabel>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextField label={t('category.form.english.categoryNm')} name="categoryNm" />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextField label={t('category.form.english.mainTitle')} name="mainTitle" />
                </FormItem>
              </FormRow>
              {!isTopCategory && (
                <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                  <FormItem>
                    <RHFTextField label={t('category.form.english.subTitle')} name="subTitle" />
                  </FormItem>
                </FormRow>
              )}
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextField label={t('category.form.english.mainDesc')} name="mainDesc" />
                </FormItem>
              </FormRow>
              {!isTopCategory && (
                <>
                  <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                    <FormItem>
                      <RHFTextField label={t('category.form.english.subDesc')} name="subDesc" />
                    </FormItem>
                  </FormRow>
                  <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                    <FormItem>
                      <RHFTextField label={t('category.form.english.pipeline')} name="pipeline" />
                    </FormItem>
                  </FormRow>
                  <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                    <FormItem>
                      <RHFTextField
                        name="pipelineList"
                        multiline
                        rows={7}
                        placeholder={`${t('category.form.english.pipelineList')}`}
                      />
                    </FormItem>
                  </FormRow>
                </>
              )}
            </FormInnerContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <>
            {formType === FormType.UPDATE && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {formType === FormType.CREATE ? t('common.button.register') : t('common.button.edit')}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
